import * as Sentry from '@sentry/react';
import { datadogRum } from '@datadog/browser-rum';

type SeverityLevel = 'error' | 'warning' | 'info';

interface LogErrorOptions {
  level?: SeverityLevel;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

/**
 * Centralized error logger — sends to both console and Sentry.
 * All Sentry usage should go through this function so the
 * provider can be swapped in one place.
 *
 * @param tag - Identifier for the error source (e.g., '[SmsVerify]')
 * @param message - Human-readable description
 * @param error - The error or value caught
 * @param extraOrOptions - Additional context, or full options object
 */
export const logError = (
  tag: string,
  message: string,
  error?: unknown,
  extraOrOptions?: Record<string, unknown> | LogErrorOptions,
): void => {
  const isOptions = extraOrOptions
    && ('level' in extraOrOptions || 'tags' in extraOrOptions);
  const opts: LogErrorOptions = isOptions
    ? extraOrOptions as LogErrorOptions
    : { extra: extraOrOptions as Record<string, unknown> | undefined };

  const { extra } = opts;
  const level = opts.level ?? 'error';
  const tags = { source: tag, ...opts.tags };

  console.error(`${tag} ${message}`, error);

  if (error instanceof Error) {
    Sentry.captureException(error, { tags, extra: { message, ...extra } });
    datadogRum.addError(error, { ...tags, ...extra });
  } else {
    Sentry.captureMessage(`${tag} ${message}`, {
      level,
      tags,
      extra: { error: String(error), ...extra },
    });
    datadogRum.addError(new Error(`${tag} ${message}`), { ...tags, ...extra, originalError: String(error) });
  }
};
